import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Tooltip,
	IconButton,
	Typography,
} from "@mui/material";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../utils/functions";
import { AssignmentReturn } from "@mui/icons-material";
import { entityNamesForCustomId, serviceNames } from "../../utils/constants";
import { getService } from "../../reducers/service";
import { notify } from "../../reducers/NotifierReducer";
import { RegularExpressions } from "../../utils/regular-expressions";
import { fetchMaintenancePhotos } from "../../reducers/MaintenanceReducer";
import ImageUploadHandler from "../../components/ImageUploadHandler";

const MAINT_STATUSES = [
	"Scheduled",
	"In Progress",
	"Completed",
	"Cancelled",
	"Pending",
];

const maintenanceSchema = yup.object().shape({
	maintenanceNum: yup
		.string()
		.trim()
		.matches(RegularExpressions.ID, "Invalid Maintenance#")
		.max(30, maxLengthMessage(30)),
	date: yup.string().required("Date is required"),
	status: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.required("Status is required"),
	statusDate: yup.string().required("Status Date is required"),
	cost: yup
		.number()
		.required("Cost is required")
		.transform((v, o) => (o === "" ? null : v)),
	performedBy: yup.string().trim().max(50, maxLengthMessage(50)),
	perPersonnelId: yup
		.number()
		.required("Assign To is required")
		.transform((v, o) => (o === "" ? null : v)),
	equipmentId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	serviceRequirementId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	fundingSourceId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	description: yup
		.string()
		.trim()
		.max(1000, maxLengthMessage(1000))
		.nullable(),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
});

const buildDefaults = (initialData) => ({
	maintenanceNum: initialData?.maintenanceNum || "",
	date: initialData?.date
		? moment(initialData.date).format("MM/DD/YYYY")
		: "",
	status: initialData?.status || "",
	statusDate: initialData?.statusDate
		? moment(initialData.statusDate).format("MM/DD/YYYY")
		: "",
	cost: initialData?.cost || "",
	performedBy: initialData?.performedBy || "",
	perPersonnelId: initialData?.perPersonnelId
		? String(initialData?.perPersonnelId)
		: "",
	equipmentId: initialData?.equipmentId || "",
	serviceRequirementId: initialData?.serviceRequirementId || "",
	fundingSourceId: initialData?.fundingSourceId || "",
	description: initialData?.description || "",
	notes: initialData?.notes || "",
});

const AddEditMaintenanceDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	viewOnly = false,
	employeeOptions = [],
	equipmentOptions = [],
	serviceRequirements = [],
	fundingSourceOptions = [],
	hideEquipment = false,
}) => {
	const dispatch = useDispatch();
	const userAgency = useSelector((state) => state.user.userAgency);
	const user = useSelector((state) => state.user);
	const isEdit = !!initialData;

	const [photos, setPhotos] = useState([]);
	const [photosFiles, setPhotosFiles] = useState([]);
	const [deletedPhotoIds, setDeletedPhotoIds] = useState([]);

	const {
		control,
		handleSubmit,
		setValue,
		reset,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(maintenanceSchema),
		defaultValues: buildDefaults(initialData),
		mode: "onChange",
	});

	useEffect(() => {
		if (open) {
			reset(buildDefaults(initialData));
		}
	}, [open, initialData, reset]);

	const getMaintenancePhotos = async (maintenanceId) => {
		try {
			const mPhotos = await fetchMaintenancePhotos(maintenanceId);
			setPhotos(mPhotos);
		} catch (error) {
			console.error(
				`Error fetching maintenance photos: ${error.message}`,
			);
		}
	};

	const handleRemovePhoto = (photoId) => {
		setPhotos((prev) => prev.filter((p) => p.id !== photoId));
		setPhotosFiles((prev) =>
			prev.filter((file) => {
				if (file?.id) {
					return file.id !== photoId;
				}
				return true;
			}),
		);

		if (!String(photoId).startsWith("new-")) {
			setDeletedPhotoIds((prev) => [...prev, photoId]);
		}
	};

	useEffect(() => {
		if (open) {
			if (initialData?.id) {
				getMaintenancePhotos(initialData.id);
			} else {
				setPhotos([]);
			}
			setPhotosFiles([]);
			setDeletedPhotoIds([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, initialData?.id]);

	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	const onSubmit = async (data) => {
		let maintenanceGeneratedId;
		if (!data.maintenanceNum?.trim()) {
			maintenanceGeneratedId = await generateCustomId();
		}
		const payload = {
			maintenanceNum: maintenanceGeneratedId
				? maintenanceGeneratedId
				: data?.maintenanceNum?.trim() || null,
			date: data.date
				? moment(data.date, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			status: data.status || null,
			statusDate: data.statusDate
				? moment(data.statusDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			cost: data.cost || null,
			performedBy: data.performedBy ? data.performedBy : null,
			perPersonnelId: data.perPersonnelId
				? Number(data.perPersonnelId)
				: null,
			equipmentId: data.equipmentId ? Number(data.equipmentId) : null,
			serviceRequirementId: data.serviceRequirementId
				? Number(data.serviceRequirementId)
				: null,
			fundingSourceId: data.fundingSourceId
				? Number(data.fundingSourceId)
				: null,
			description: data.description?.trim() || null,
			notes: data.notes?.trim() || null,
			photos:
				photosFiles.length > 0
					? photosFiles.map((p) => p.file || p)
					: [],
			deletedPhotoIds,
			agencyId: user?.userAgency || null,
		};

		onSave(payload);
	};

	const statusOptions = MAINT_STATUSES.map((s) => ({
		value: s,
		label: s,
	}));

	const svcReqOptions = (serviceRequirements || []).map((sr) => ({
		value: sr.ptsCodeId,
		label: `${sr.description} (${sr.label}) — every ${Number(sr.intervalDays).toLocaleString()} Days`,
	}));

	const dialogTitle = isEdit
		? viewOnly
			? "View Maintenance Record"
			: "Edit Maintenance Record"
		: "Add Maintenance Record";

	const generateCustomId = async () => {
		const data = {
			EntityName: entityNamesForCustomId.MaintenanceId,
			agencyID: userAgency,
		};
		const service = getService(serviceNames.customId);
		service.timeout = 20000;

		const id = await service.create(data);
		if (id?.includes("Agency ID does not match")) {
			dispatch(
				notify(
					"Unable to generate Maintenance ID because the Agency ID does not match.",
					"warning",
				),
			);
			return null;
		}
		return id;
	};

	useEffect(() => {
		setValue("maintenanceNum", "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency]);

	const handleAutoGenerateClick = async () => {
		const id = await generateCustomId();
		setValue("maintenanceNum", id, { shouldValidate: true });
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.75 : undefined,
					mt: 1,
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(3, 1fr)",
						gap: 2,
						pt: 1,
					}}
				>
					<Box display="flex" alignItems="center">
						<Box flexGrow={1}>
							<TextFieldController
								name="maintenanceNum"
								label="Maintenance#"
								control={control}
								errors={errors}
							/>
						</Box>
						<Box>
							<Tooltip title="Auto generate maintenance number">
								<IconButton
									color="primary"
									aria-label="Auto generate maintenance number"
									size="large"
									onClick={handleAutoGenerateClick}
								>
									<AssignmentReturn />
								</IconButton>
							</Tooltip>
						</Box>
					</Box>
					<Box>
						<DatePickerController
							name="date"
							label="Date*"
							control={control}
							errors={errors}
						/>
					</Box>
					<Box>
						<DropdownController
							name="status"
							label="Status*"
							options={statusOptions}
							control={control}
							errors={errors}
						/>
					</Box>
					<Box>
						<DatePickerController
							name="statusDate"
							label="Status Date*"
							control={control}
							errors={errors}
						/>
					</Box>

					<Box>
						<TextFieldController
							name="cost"
							label="Cost ($)*"
							control={control}
							errors={errors}
							type="number"
							placeholder="0.00"
						/>
					</Box>
					<Box>
						<TextFieldController
							name="performedBy"
							label="Performed By"
							control={control}
							errors={errors}
						/>
					</Box>
					<Box>
						<DropdownController
							name="perPersonnelId"
							label="Assigned To*"
							options={employeeOptions.map((e) => ({
								value: e.value,
								label: e.label,
							}))}
							control={control}
							errors={errors}
							placeholder="— None —"
						/>
					</Box>

					{!hideEquipment && (
						<Box sx={{ gridColumn: "1 / -1" }}>
							<DropdownController
								name="equipmentId"
								label="Equipment (optional)"
								options={equipmentOptions.map((eq) => ({
									value: eq.value,
									label: eq.label,
								}))}
								control={control}
								errors={errors}
							/>
						</Box>
					)}

					<Box sx={{ gridColumn: "1 / -1" }}>
						<DropdownController
							name="serviceRequirementId"
							label="Fulfills Service Requirement (optional)"
							options={svcReqOptions}
							control={control}
							errors={errors}
							placeholder="— Not linked to a service requirement —"
						/>
					</Box>

					<Box sx={{ gridColumn: "1 / -1" }}>
						<DropdownController
							name="fundingSourceId"
							label="Funding Source (optional)"
							options={fundingSourceOptions.map((fs) => ({
								value: fs.value,
								label: fs.label,
							}))}
							control={control}
							errors={errors}
							placeholder="— Not linked to funding source —"
						/>
					</Box>

					<Box sx={{ gridColumn: "1 / -1" }}>
						<TextFieldController
							name="description"
							label="Description"
							control={control}
							errors={errors}
							multiline
							rows={3}
							placeholder="Describe the maintenance performed or required..."
						/>
					</Box>

					<Box sx={{ gridColumn: "1 / -1" }}>
						<TextFieldController
							name="notes"
							label="Notes"
							control={control}
							errors={errors}
							multiline
							rows={2}
							placeholder="Additional notes, parts used, follow-up actions..."
						/>
					</Box>

					<Box sx={{ gridColumn: "1 / -1", pt: 1 }}>
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								mb: 1,
								display: "block",
							}}
						>
							Attachments
						</Typography>

						<ImageUploadHandler
							onCapture={(file) => {
								if (!file) return;
								setPhotosFiles((prev) => [...prev, file]);
								const src = URL.createObjectURL(file);
								setPhotos((prev) => [
									...prev,
									{
										id: `new-${Date.now()}-${Math.random()}`,
										src,
										name: file.name,
										size: file.size,
									},
								]);
							}}
						/>

						{photos.length > 0 && (
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns:
										"repeat(auto-fill, minmax(90px, 1fr))",
									gap: 1,
									marginTop: 2,
								}}
							>
								{photos.map((p) => (
									<Box
										key={p.id}
										sx={{
											position: "relative",
											aspectRatio: "1",
											borderRadius: 1,
											overflow: "hidden",
											border: "1px solid",
											borderColor: "divider",
										}}
									>
										<img
											src={p.src}
											alt={p.name}
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
										/>
										<IconButton
											size="small"
											onClick={() =>
												handleRemovePhoto(p.id)
											}
											sx={{
												position: "absolute",
												top: 2,
												right: 2,
												bgcolor: "rgba(0,0,0,0.6)",
												color: "#fff",
												width: 22,
												height: 22,
												"&:hover": {
													bgcolor: "rgba(0,0,0,0.8)",
												},
											}}
										>
											✕
										</IconButton>
									</Box>
								))}
							</Box>
						)}
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATCloseButton onClick={onClose} tooltipText="Close" />
				{!viewOnly && (
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText="Save"
						disabled={!isValid}
					/>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default AddEditMaintenanceDialog;
